import React, { useEffect } from 'react';

import router from 'next/router';

import { AxiosError } from 'axios';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';

import { fetchOrgEventsV3, withOrg } from 'api/organizations';
import GridPreloader from 'components/Loaders/GridPreloader';
import { MetaTags } from 'components/MetaTags';
import Events from 'containers/Organization/Events';
import { IError, handleAxiosError } from 'utils/handleAxiosError';
import { isObjectId } from 'utils/validations';


type Props = InferGetStaticPropsType<typeof getStaticProps>;

const OrganizationEvents: NextPage<Props> = ({ data, error }) => {
  // redirect to new CL UI
  useEffect(() => {
    const { organization_id } = router.query;
    document.location.href = `${
      document.location.href.split('/o/')[0]
    }/cl/organizations/${organization_id}/events`;
  }, []);

  if (error) {
    return (
      <div>
        {error.code} | {error.message}{' '}
      </div>
    );
  }
  if (!data) {
    return <GridPreloader />;
  }
  return (
    <>
      <MetaTags title={`Conferences | ${data.organization.name}`} />

      <Events events={data.events} organizationId={data.organizationId} />
    </>
  );
};

export const getStaticProps: GetStaticProps<
  {
    data?: {
      events: OrganizationEventsResponseV3;
      organizationId: string;
      organization: MorressierOrganization;
    };
    error?: IError;
  },
  { organization_id: string; page: string }
> = async ctx => {
  const organizationId = ctx.params?.organization_id;
  const page = Number(ctx.params?.page) || 1;
  const limit = 15;

  try {
    if (!isObjectId(organizationId)) throw new Error(`Invalid organization ID: ${organizationId}`);

    const [organization, events] = await withOrg(organizationId, () =>
      fetchOrgEventsV3({
        organizationId,
        query: {
          offset: (page - 1) * limit,
          limit,
          excludeUnpulishedEvents: true,
        },
      }),
    );

    return {
      props: { data: { events, organization, organizationId } },
      revalidate: 3,
    };
  } catch (err) {
    const error = handleAxiosError(err as AxiosError);

    return {
      props: { error },
      revalidate: 60,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => ({ paths: [], fallback: 'blocking' });

export default OrganizationEvents;
